import { AuthContext, sanitize } from '@core/shared';
import { OptionsApi } from '@core/swagger-client';
import { Button, ErrorMessage, ErrorMessagePage, Heading, Input, LoaderPage, TextArea } from '@core/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { encode } from 'js-base64';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PreviewPanelOrganism } from '../../organism';
import { EmailHeaderImage } from './EmailHeaderImage';
import { fillHeaderPlaceholders, hasHeaderImagePlaceholder } from './headerPlaceholders';

export const ManageEmailStylingPage = () => {
    const [template, setTemplate] = useState('');
    const [paragraphStyle, setParagraphStyle] = useState('');
    const [buttonStyle, setButtonStyle] = useState('');
    const [codeStyle, setCodeStyle] = useState('');
    const [tableStyle, setTableStyle] = useState('');
    const [tableHeaderRowStyle, setTableHeaderRowStyle] = useState('');
    const [tableHeaderCellStyle, setTableHeaderCellStyle] = useState('');
    const [tableBodyRowStyle, setTableBodyRowStyle] = useState('');
    const [tableBodyCellStyle, setTableBodyCellStyle] = useState('');
    const [headingStyles, setHeadingStyles] = useState<{ [key: string]: string }>({});
    const [html, setHtml] = useState('');
    // Upload / use default change the URL at once; the page keeps it here instead of refetching the style,
    // whose load effect below would reset every unsaved field.
    const [headerImageUrl, setHeaderImageUrl] = useState<string | null>(null);
    const [headerImageAltText, setHeaderImageAltText] = useState('');
    const { t } = useTranslation();

    const canManageEmailStyle = useContext(AuthContext).canManageEmailStyle;
    if (!canManageEmailStyle) {
        return <ErrorMessagePage statusCode={404} />;
    }

    const { isLoading, data: emailStyle, error, refetch: refetchStyling } = useQuery(OptionsApi.emailStyle.get({ refetchOnWindowFocus: false }));

    const getHeadingStyle = (level: number) => {
        let style = headingStyles[level.toString()];
        while (!style && level > 0) {
            level--;
            style = headingStyles[level.toString()];
        }
        return style;
    };

    useEffect(() => {
        let html = fillHeaderPlaceholders(template, {
            url: headerImageUrl,
            defaultUrl: emailStyle?.defaultHeaderImageUrl ?? '',
            altText: headerImageAltText
        }).replaceAll('@Subject', 'Lorem ipsum odor amet, consectetuer adipiscing elit');
        const body = `
            <h1 style="${getHeadingStyle(1)}">Lorem ipsum odor amet, consectetuer adipiscing elit</h1>
            <p style="${paragraphStyle}">Lorem ipsum odor amet, consectetuer adipiscing elit. Adipiscing aliquet tortor leo hac per sodales habitasse. Sodales vitae vitae donec congue suscipit auctor torquent porta.</p>
            <p style="${paragraphStyle}">
            <table style="-webkit-text-size-adjust: 100%;-ms-text-size-adjust: 100%;mso-table-lspace: 0pt;mso-table-rspace: 0pt;border-collapse: collapse !important;width: 100% !important;">
            <tr><td align="center" style="-webkit-text-size-adjust: 100%;-ms-text-size-adjust: 100%;mso-table-lspace: 0pt;mso-table-rspace: 0pt;">
            <p style="-webkit-text-size-adjust: 100%;-ms-text-size-adjust: 100%;font-size: 16px;font-weight: normal;margin-bottom: 20px;">
            <a href="#" class="button" style="${buttonStyle}">Click here</a>
            </p></td></tr></table>
            </p>
            <p style="${paragraphStyle}">Lorem ipsum odor amet, consectetuer adipiscing elit. Netus vel ligula velit luctus lacinia facilisis. Ligula sociosqu congue at, lacinia suscipit duis consequat.</p>
            <p style="${codeStyle}">00000000-0000-0000-0000-000000000000</p>
            <h2 style="${getHeadingStyle(2)}">Lorem ipsum odor amet, consectetuer adipiscing elit</h2>
            <p style="${paragraphStyle}">Lorem ipsum odor amet.</p>
            <h3 style="${getHeadingStyle(3)}">Lorem ipsum odor amet, consectetuer adipiscing elit</h3>
            <p style="${paragraphStyle}">Lorem ipsum odor amet.</p>
            <h4 style="${getHeadingStyle(4)}">Lorem ipsum odor amet, consectetuer adipiscing elit</h4>
            <p style="${paragraphStyle}">Lorem ipsum odor amet.</p>
            <h5 style="${getHeadingStyle(5)}">Lorem ipsum odor amet, consectetuer adipiscing elit</h5>
            <p style="${paragraphStyle}">Lorem ipsum odor amet.</p>
            <h6 style="${getHeadingStyle(6)}">Lorem ipsum odor amet, consectetuer adipiscing elit</h6>
            <p style="${paragraphStyle}">Lorem ipsum odor amet.</p>
            <table style="${tableStyle}">
              <thead>
                <tr style="${tableHeaderRowStyle}">
                  <th style="${tableHeaderCellStyle}">Header 1</th>
                  <th style="${tableHeaderCellStyle}">Header 2</th>
                </tr>
              </thead>
              <tbody>
                <tr style="${tableBodyRowStyle}">
                  <td style="${tableBodyCellStyle}">Row 1, Cell 1</td>
                  <td style="${tableBodyCellStyle}">Row 1, Cell 2</td>
                </tr>
                <tr style="${tableBodyRowStyle}">
                  <td style="${tableBodyCellStyle}">Row 2, Cell 1</td>
                  <td style="${tableBodyCellStyle}">Row 2, Cell 2</td>
                </tr>
                </tbody>
            </table>
        `;
        html = html.replaceAll('@Body', body);
        setHtml(html);
    }, [
        template,
        paragraphStyle,
        buttonStyle,
        codeStyle,
        headingStyles,
        tableStyle,
        tableHeaderRowStyle,
        tableHeaderCellStyle,
        tableBodyRowStyle,
        tableBodyCellStyle,
        headerImageUrl,
        headerImageAltText,
        emailStyle
    ]);

    useEffect(() => {
        if (!emailStyle) {
            return;
        }

        setParagraphStyle(emailStyle.paragraphStyle);
        setButtonStyle(emailStyle.buttonStyle);
        setCodeStyle(emailStyle.codeStyle);
        setHeadingStyles(emailStyle.headingStyles);
        setTableStyle(emailStyle.tableStyle);
        setTableHeaderRowStyle(emailStyle.tableHeaderRowStyle);
        setTableHeaderCellStyle(emailStyle.tableHeaderCellStyle);
        setTableBodyRowStyle(emailStyle.tableBodyRowStyle);
        setTableBodyCellStyle(emailStyle.tableBodyCellStyle);
        setTemplate(emailStyle.template);
        setHeaderImageUrl(emailStyle.headerImageUrl ?? null);
        setHeaderImageAltText(emailStyle.headerImageAltText ?? '');
    }, [emailStyle]);

    const validateStyling = () => {
        for (let style of [
            paragraphStyle,
            buttonStyle,
            codeStyle,
            tableStyle,
            tableHeaderRowStyle,
            tableHeaderCellStyle,
            tableBodyRowStyle,
            tableBodyCellStyle
        ]) {
            let element = `<div style="${style}">Test</div>`;
            if (sanitize(element) !== element) {
                console.error('Invalid style:', { style, element, sanitized: sanitize(element) });
                return false;
            }
        }

        return true;
    };

    const setStylingMutation = useMutation(OptionsApi.emailStyle.set());
    const saveStyling = () => {
        if (!validateStyling()) {
            return;
        }

        const doctype = '<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Strict//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-strict.dtd">\n';
        const config = { WHOLE_DOCUMENT: true, ADD_TAGS: ['link', 'meta', 'tbody'], ADD_ATTR: ['http-equiv', 'content'] };
        setStylingMutation.mutate(
            {
                paragraphStyle,
                buttonStyle,
                codeStyle,
                headingStyles,
                tableStyle,
                tableHeaderRowStyle,
                tableHeaderCellStyle,
                tableBodyRowStyle,
                tableBodyCellStyle,
                headerImageAltText,
                // Convert to bas64 to avoid issues with protection of ASP.NET Core
                template: encode(doctype + sanitize(sanitize(template, config), config))
            },
            {
                onSuccess: () => refetchStyling()
            }
        );
        return false;
    };

    if (error) {
        return <ErrorMessagePage apiError={error} />;
    }

    if (isLoading) {
        return <LoaderPage />;
    }

    const isValidateStyle = (style: string) => {
        let element = `<div style="${style}">Test</div>`;
        return sanitize(element) === element;
    };

    return (
        <>
            <main className="flex flex-col md:flex-row flex-1">
                <div className="bg-white flex-1 px-4 pb-4 pt-12 md:pt-24">
                    <div className="sticky top-4 max-w-160 mx-auto">
                        <Heading variant="h1" classNames="mt-2 mb-6">
                            {t('Email.Style.PageHeading')}
                        </Heading>
                        {setStylingMutation.error && <ErrorMessage apiError={setStylingMutation.error} />}
                        <EmailHeaderImage
                            headerImageUrl={headerImageUrl}
                            defaultHeaderImageUrl={emailStyle?.defaultHeaderImageUrl ?? ''}
                            onChange={setHeaderImageUrl}
                        />
                        <form onSubmit={(e) => e.preventDefault()}>
                            <Input
                                label={t('Email.Style.HeaderImage.Label.AltText')}
                                value={headerImageAltText}
                                onChange={(e) => setHeaderImageAltText(e.target.value)}
                                maxLength={250}
                                infoMessage={t('Email.Style.HeaderImage.AltTextInfo')}
                            ></Input>
                            <Input
                                label={t('Email.Style.Label.Paragraph')}
                                value={paragraphStyle}
                                onChange={(e) => setParagraphStyle(e.target.value)}
                                errorMessage={isValidateStyle(paragraphStyle) ? '' : t('Email.Style.Error.InvalidStyle')}
                            ></Input>
                            <Input
                                label={t('Email.Style.Label.Button')}
                                value={buttonStyle}
                                onChange={(e) => setButtonStyle(e.target.value)}
                                errorMessage={isValidateStyle(buttonStyle) ? '' : t('Email.Style.Error.InvalidStyle')}
                            ></Input>
                            <Input
                                label={t('Email.Style.Label.Code')}
                                value={codeStyle}
                                onChange={(e) => setCodeStyle(e.target.value)}
                                errorMessage={isValidateStyle(codeStyle) ? '' : t('Email.Style.Error.InvalidStyle')}
                            ></Input>
                            {[1, 2, 3, 4, 5, 6].map((level) => (
                                <Input
                                    label={t('Email.Style.Label.Heading', { level: level })}
                                    value={headingStyles[level.toString()]}
                                    onChange={(e) => setHeadingStyles({ ...headingStyles, [level.toString()]: e.target.value })}
                                    errorMessage={isValidateStyle(getHeadingStyle(level)) ? '' : t('Email.Style.Error.InvalidStyle')}
                                ></Input>
                            ))}
                            <Input
                                label={t('Email.Style.Label.Table')}
                                value={tableStyle}
                                onChange={(e) => setTableStyle(e.target.value)}
                                errorMessage={isValidateStyle(tableStyle) ? '' : t('Email.Style.Error.InvalidStyle')}
                            ></Input>
                            <Input
                                label={t('Email.Style.Label.TableHeaderRow')}
                                value={tableHeaderRowStyle}
                                onChange={(e) => setTableHeaderRowStyle(e.target.value)}
                                errorMessage={isValidateStyle(tableHeaderRowStyle) ? '' : t('Email.Style.Error.InvalidStyle')}
                            ></Input>
                            <Input
                                label={t('Email.Style.Label.TableHeaderCell')}
                                value={tableHeaderCellStyle}
                                onChange={(e) => setTableHeaderCellStyle(e.target.value)}
                                errorMessage={isValidateStyle(tableHeaderCellStyle) ? '' : t('Email.Style.Error.InvalidStyle')}
                            ></Input>
                            <Input
                                label={t('Email.Style.Label.TableBodyRow')}
                                value={tableBodyRowStyle}
                                onChange={(e) => setTableBodyRowStyle(e.target.value)}
                                errorMessage={isValidateStyle(tableBodyRowStyle) ? '' : t('Email.Style.Error.InvalidStyle')}
                            ></Input>
                            <Input
                                label={t('Email.Style.Label.TableBodyCell')}
                                value={tableBodyCellStyle}
                                onChange={(e) => setTableBodyCellStyle(e.target.value)}
                                errorMessage={isValidateStyle(tableBodyCellStyle) ? '' : t('Email.Style.Error.InvalidStyle')}
                            ></Input>
                            {!hasHeaderImagePlaceholder(template) && <p className="mb-4">{t('Email.Style.HeaderImage.Warning.NoPlaceholder')}</p>}
                            <TextArea
                                label={t('Email.Style.Label.Template')}
                                value={template}
                                onChange={(e) => setTemplate(e.target.value)}
                                errorMessage={isValidateStyle(template) ? '' : t('Email.Style.Error.InvalidStyle')}
                                textAreaClassNames="min-h-[40vh] field-sizing-content"
                            ></TextArea>
                            <Button type="submit" variant="primary" classNames="mt-4" onClick={saveStyling}>
                                {t('Generic.Button.Save')}
                            </Button>
                            <p className="mt-6">{t('Email.Style.Warning')}</p>
                        </form>
                    </div>
                </div>
                <div className="bg-info bg-center bg-cover bg-no-repeat flex-1 flex flex-col items-center px-4 pt-4 md:pt-16 xl:pt-12 pb-6">
                    <PreviewPanelOrganism html={html} />
                </div>
            </main>
        </>
    );
};
